import type { ReactNode } from "react";
import {
  getColWidth,
  getDayBgClass,
  getHolidayName,
  getJobBarColorForTime
} from "../../utils";
import { useHolidays } from "@/hooks/use-holidays";
import { BAR_AREA_TOP, DATE_NUM_H, DAY_HEADER_H, DAY_NAMES } from "../constant";
import {
  buildWeeksWithFill,
  getAllWeekBars,
  getAssignmentsForDay
} from "./helper";
import { Wrench, Plane, User, Settings } from "lucide-react";
import dayjs from "dayjs";
import {
  MonthlyCellHoverContent,
  MonthlyCellAvailabilityContent
} from "./utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import ScheduleHoverCard from "../schedule-hover-card";
import {
  MonthDayUnavailableOverlay,
  type MonthDayAvailability
} from "../availability-bands";
import type { ProcessedAssignment } from "../../type";

export interface BarRenderContext {
  assignment: ProcessedAssignment;
  barHeight: number;
  isCompact: boolean;
  isStartClipped: boolean;
  isEndClipped: boolean;
}

interface RightColumnSize {
  barHeight: number;
  cellHeight: number;
  maxBars: number;
  barGap: number;
}

interface RightColumnProps {
  days: Date[];
  weeks: (Date | null)[][];
  allAssignments: ProcessedAssignment[];
  size: RightColumnSize;
  isCompact: boolean;
  zoomLevel?: number;
  /**
   * Optional render prop for bar inner content.
   * When provided, replaces the default job-view bar body.
   * Receives the assignment and sizing context; must return a ReactNode.
   */
  renderBarContent?: (ctx: BarRenderContext) => ReactNode;
  /**
   * Optional per-day AM/PM unavailability for the merged calendar ("YYYY-MM-DD" ->
   * {am, pm}), aggregated across the displayed resources. Absent day = available.
   * Only tech/machine tabs pass this; the job tab leaves it undefined (no red). (WOR-2187.)
   */
  availabilityByDay?: Map<string, MonthDayAvailability>;
}

export default function RightColumn({
  days,
  weeks,
  allAssignments,
  size,
  isCompact,
  zoomLevel,
  renderBarContent,
  availabilityByDay
}: RightColumnProps) {
  const {
    barHeight: BAR_H,
    cellHeight: CELL_H,
    maxBars: MAX_BARS,
    barGap: BAR_GAP
  } = size;

  const holidays = useHolidays();
  const COL_W = getColWidth("monthly", zoomLevel); // 147px at 20% → 735px at 100%
  const TOTAL_DAY_COLUMNS = 7;
  const totalWeekWidth = COL_W * TOTAL_DAY_COLUMNS;
  const dayWidthPercent = 100 / TOTAL_DAY_COLUMNS;

  const weeksWithFill = buildWeeksWithFill(days);

  return (
    <div
      className="aero:flex-1 aero:h-full aero:overflow-auto"
      style={{ paddingLeft: 20, paddingTop: 20 }}
    >
      {/* Single scrollable container — header row is sticky so it always aligns with body columns */}
      <div className="aero:min-w-full" style={{ minWidth: totalWeekWidth }}>
        {/* Day-name header row — sticky so it stays visible while scrolling */}
        <div
          className="aero:flex aero:border-b aero:border-t aero:border-neutral-300 aero:bg-white aero:top-0 aero:z-10"
          style={{ width: "100%", minWidth: totalWeekWidth }}
        >
          {DAY_NAMES.map((name) => (
            <div
              key={name}
              className="aero:flex aero:items-center aero:justify-center aero:text-[11px] aero:font-semibold aero:uppercase aero:tracking-widest aero:text-muted-foreground aero:border-r aero:border-neutral-300 aero:last:border-r-0"
              style={{
                flex: "1 1 0",
                minWidth: COL_W,
                height: DAY_HEADER_H,
                paddingLeft: 10,
                paddingRight: 10
              }}
            >
              {name}
            </div>
          ))}
        </div>
        {weeks.map((week, wi) => {
          const weekBars = getAllWeekBars(allAssignments, week);
          const visibleBars = weekBars.filter((b) => b.lane < MAX_BARS);
          const overflowCount = weekBars.filter(
            (b) => b.lane >= MAX_BARS
          ).length;
          const filledWeek = weeksWithFill[wi] ?? [];

          return (
            <div
              key={wi}
              className="aero:relative aero:flex"
              style={{
                height: CELL_H,
                width: "100%",
                minWidth: totalWeekWidth
              }}
            >
              {/* Day cell backgrounds + date numbers */}
              {filledWeek.map((cell, di) => {
                const { date: day, isCurrentMonth } = cell;
                const d = dayjs(day);
                const cellAssignments = isCurrentMonth
                  ? getAssignmentsForDay(allAssignments, day)
                  : [];
                const isFirstOfMonth = isCurrentMonth && d.date() === 1;
                // Weekend grey / holiday yellow / today, precedence in getDayBgClass (WOR-2187).
                const dayBg = getDayBgClass(day, holidays);
                const holidayName = getHolidayName(day, holidays);
                // Aggregated AM/PM unavailability for tech/machine tabs (WOR-2187 R3).
                const dayAvail = isCurrentMonth
                  ? availabilityByDay?.get(d.format("YYYY-MM-DD"))
                  : undefined;
                const cellEl = (
                  <div
                    className={cn(
                      "aero:relative aero:border-r aero:last:border-r aero:border-b aero:border-neutral-300 aero:h-full aero:transition-colors",
                      dayBg,
                      isCurrentMonth && "hover:aero:bg-[#F4FFFF]"
                    )}
                    style={{
                      flex: "1 1 0",
                      minWidth: COL_W,
                      height: CELL_H
                    }}
                  >
                    {dayAvail && (
                      <MonthDayUnavailableOverlay
                        am={dayAvail.am}
                        pm={dayAvail.pm}
                      />
                    )}
                    <div
                      className={cn(
                        "aero:relative aero:z-[1] aero:px-2 aero:pt-1 aero:text-xs aero:leading-none",
                        isCurrentMonth
                          ? "aero:text-neutral-500"
                          : "aero:text-neutral-400"
                      )}
                      style={{ height: DATE_NUM_H }}
                    >
                      {isFirstOfMonth ? d.format("DD MMM") : d.format("DD")}
                    </div>
                    {holidayName && isCurrentMonth && (
                      <div
                        className="aero:relative aero:z-[1] aero:px-2 aero:text-[10px] aero:font-medium aero:leading-tight aero:text-amber-700 aero:truncate"
                        title={holidayName}
                      >
                        {holidayName}
                      </div>
                    )}
                  </div>
                );
                // Show the cell tooltip when there are assignments OR the day is
                // unavailable. A single cell-level tooltip carries both (availability on
                // top, assignment list below) so the wash overlay can stay pure-visual and
                // avoid a nested tooltip (WOR-2187).
                const hasAssignments = cellAssignments.length > 0;
                if (!isCurrentMonth || (!hasAssignments && !dayAvail)) {
                  return (
                    <div key={di} className="aero:contents">
                      {cellEl}
                    </div>
                  );
                }
                return (
                  <TooltipProvider key={di} delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>{cellEl}</TooltipTrigger>
                      <TooltipContent
                        side="right"
                        align="start"
                        className="aero:p-0 aero:border-0 aero:bg-transparent aero:shadow-none"
                        sideOffset={4}
                      >
                        <div className="aero:flex aero:flex-col aero:gap-1">
                          {dayAvail && (
                            <MonthlyCellAvailabilityContent avail={dayAvail} />
                          )}
                          {hasAssignments && (
                            <MonthlyCellHoverContent
                              assignments={cellAssignments}
                            />
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}

              {/* Absolutely positioned spanning bars */}
              {visibleBars.map((bar, bi) => {
                const {
                  assignment: a,
                  startCol,
                  endCol,
                  lane,
                  isStartClipped,
                  isEndClipped
                } = bar;
                // Match the week/day views: color by OT time + backend variant (not
                // priority) so a bar that is red in week stays red in month (WOR-2187).
                const {
                  color: barColor,
                  text: barText,
                  accent: barAccent
                } = getJobBarColorForTime(a.startDate, a.barVariant);
                const span = endCol - startCol + 1;
                const left = `calc(${startCol * dayWidthPercent}% + 2px)`;
                const width = `calc(${span * dayWidthPercent}% - 4px)`;
                const top = BAR_AREA_TOP + lane * (BAR_H + BAR_GAP);
                const endDateForDisplay = a.eddDate ?? a.startDate;

                // Inner content — delegate to renderBarContent if provided,
                // otherwise fall back to the default job-view body.
                const barBody = renderBarContent ? (
                  renderBarContent({
                    assignment: a,
                    barHeight: BAR_H,
                    isCompact,
                    isStartClipped,
                    isEndClipped
                  })
                ) : isCompact ? (
                  /* ── Compact bar (20–30%) — single line: time · workstage ── */
                  <div className="aero:flex aero:items-center aero:gap-1 aero:px-1 aero:min-w-0 aero:flex-1 aero:overflow-hidden">
                    <span className="aero:text-[9px] aero:text-neutral-900 aero:shrink-0 aero:whitespace-nowrap">
                      {dayjs(a.startDate).format("HH:mm")}–
                      {dayjs(endDateForDisplay).format("HH:mm")}
                    </span>
                    <span
                      className={cn(
                        "aero:text-[10px] aero:font-bold aero:truncate",
                        barText
                      )}
                    >
                      {a.processDesc}
                    </span>
                  </div>
                ) : (
                  /* ── Full card (50%+) — time + workstage + job + icons ── */
                  <div className="aero:flex aero:flex-col aero:gap-1.5 aero:p-1.5 aero:flex-1 aero:min-w-0 aero:overflow-hidden">
                    <div className="aero:flex aero:flex-col aero:gap-0">
                      <span className="aero:text-[9px] aero:text-neutral-900 aero:whitespace-nowrap">
                        {dayjs(a.startDate).format("H:mm")} –{" "}
                        {dayjs(endDateForDisplay).format("H:mm")}
                      </span>
                      <span
                        className={cn(
                          "aero:text-[10px] aero:font-bold aero:truncate",
                          barText
                        )}
                      >
                        {a.processDesc}
                      </span>
                      <span
                        className={cn(
                          "aero:text-[10px] aero:truncate",
                          barText
                        )}
                      >
                        {a.jobOrderNo}
                      </span>
                    </div>
                    {BAR_H >= 80 && (
                      <div className="aero:flex aero:flex-col aero:gap-1">
                        <div className="aero:flex aero:items-center aero:gap-1">
                          <Wrench className="aero:h-3 aero:w-3 aero:flex-shrink-0 aero:text-neutral-600" />
                          <span className="aero:text-[10px] aero:text-neutral-900 aero:truncate">
                            {a.partNo
                              ? `${a.partNo}${a.partDescription ? ` - ${a.partDescription}` : ""}`
                              : "—"}
                          </span>
                        </div>
                      </div>
                    )}
                    {BAR_H >= 95 && (
                      <div className="aero:flex aero:items-center aero:gap-1">
                        <Plane className="aero:h-3 aero:w-3 aero:flex-shrink-0 aero:text-neutral-600" />
                        {a.customerName ? (
                          <span className="aero:text-[10px] aero:text-neutral-900 aero:truncate">
                            {a.customerName}
                          </span>
                        ) : (
                          <span className="aero:text-[10px] aero:italic aero:text-neutral-500 aero:truncate">
                            No Customer
                          </span>
                        )}
                      </div>
                    )}
                    {BAR_H >= 115 && (
                      <div className="aero:flex aero:items-center aero:gap-1">
                        <User className="aero:h-3 aero:w-3 aero:flex-shrink-0 aero:text-neutral-600" />
                        {a.technicianName ? (
                          <span className="aero:text-[10px] aero:text-neutral-900 aero:truncate">
                            {a.technicianName}
                          </span>
                        ) : (
                          <span className="aero:text-[10px] aero:italic aero:text-neutral-500 aero:truncate">
                            Unassigned
                          </span>
                        )}
                      </div>
                    )}
                    {BAR_H >= 130 && (
                      <div className="aero:flex aero:items-center aero:gap-1">
                        <Settings className="aero:h-3 aero:w-3 aero:flex-shrink-0 aero:text-neutral-600" />
                        {a.machineName ? (
                          <span className="aero:text-[10px] aero:text-neutral-900 aero:truncate">
                            {a.machineName}
                          </span>
                        ) : (
                          <span className="aero:text-[10px] aero:italic aero:text-neutral-500 aero:truncate">
                            No Machine
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );

                return (
                  <TooltipProvider key={`${String(a.id ?? bi)}-${wi}`}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            "aero:absolute aero:flex aero:items-stretch aero:rounded aero:overflow-hidden",
                            "aero:border aero:border-dashed aero:cursor-default aero:z-[2]",
                            barColor
                          )}
                          style={{ left, width, top, height: BAR_H }}
                        >
                          {!isStartClipped && (
                            <div
                              className={cn(
                                "aero:w-[3px] aero:flex-shrink-0 aero:rounded-l",
                                barAccent
                              )}
                            />
                          )}
                          {barBody}
                          {isEndClipped && (
                            <div className="aero:w-2 aero:flex-shrink-0 aero:bg-white/30" />
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="aero:p-0 aero:border-0 aero:bg-transparent aero:shadow-none">
                        <ScheduleHoverCard
                          variant="job"
                          data={{
                            jobOrderNo: a.jobOrderNo,
                            processDesc: a.processDesc,
                            partNo: a.partNo,
                            partDescription: a.partDescription,
                            customerName: a.customerName,
                            startDate: a.startDate,
                            eddDate: a.eddDate,
                            duration: a.duration,
                            technicianName: a.technicianName,
                            machineName: a.machineName,
                            priorityColor: barColor
                          }}
                        />
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}

              {overflowCount > 0 && (
                <div
                  className="aero:absolute aero:right-2 aero:text-[9px] aero:font-semibold aero:text-muted-foreground"
                  style={{ bottom: 4 }}
                >
                  +{overflowCount} more
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* end right column */}
    </div>
  );
}
